import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CleanInputIcon from 'components/Form/shared/CleanInputIcon';
import Conditional from 'components/Conditional';
import { Input } from '../Input';
import MagnifyingIcon from 'assets/icons/magnifying.svg';
import {
  StyledSelectWrapper,
  StyledSelect,
  StyledSearchBar,
  StyledDropdownContent,
  StyledDropdownItem,
  DropdownOption,
  StyledError,
  StyledDropdownContentWrapper,
} from './styled';

const Select = forwardRef((props, ref) => {
  const {
    size,
    placeholder,
    isActive = true,
    onChange,
    onSelect = () => {},
    onInputClear = () => {},
    value,
    isRequired = false,
    options,
    isSearchDisabled = false,
    isClearInputActive,
    className = '',
    error,
    errormessage,
    disabled,
    children,
    defaultValue,
    'data-cy': dataCy,
    cleanFields,
    name,
    setValueSubGroup,
    hasOnlyUnitsWithSubgroup = true,
    inputDifferent = false, // Nova propriedade para controlar estilos diferentes nos inputs
    inputWidth, // Nova propriedade para controlar a largura do input
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [filterWord, setFilterWord] = useState('');
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [selectedLabel, setSelectedLabel] = useState('');
  const selectRef = useRef(ref);
  const [newOptions, setNewOptions] = useState([]);
  useEffect(() => {
    if (name?.includes('document')) {
      let othersOptions = [];
      othersOptions = options.filter(option => option.value?.includes('pdf'));
      setNewOptions(othersOptions);
    } else {
      setNewOptions(options);
    }
  }, [name, options]);

  const handleValueChange = useCallback(
    valueToFind => {
      if (valueToFind) {
        const current = newOptions?.find(option => option.value === valueToFind);
        if (current && hasOnlyUnitsWithSubgroup) {
          setSelectedValue(current.value);
          setSelectedLabel(current.label);
        }
      } else {
        setSelectedValue('');
        setSelectedLabel('');
      }
    },
    [newOptions, hasOnlyUnitsWithSubgroup],
  );

  useEffect(() => {
    handleValueChange(defaultValue);
  }, [handleValueChange, defaultValue]);

  useEffect(() => {
    handleValueChange(value);
  }, [handleValueChange, value]);

  const handleChange = option => {
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    onChange && onChange(option.value);
  };

  const openDropDownContent = () => {
    if (name === 'unitGroup') {
      setValueSubGroup('unitSubgroup', '');
    }
    if (newOptions.length === 1 && selectedLabel && hasOnlyUnitsWithSubgroup) {
      return;
    }

    if (newOptions.length > 0) {
      setIsOpen(() => !isOpen);
    }
  };

  const filteredOptions = useMemo(() => {
    const filterOptionsByWord = option => {
      if (!filterWord) {
        return option;
      }

      const wordMatches = `${option?.label}`.toLowerCase().includes(filterWord.toLowerCase());

      if (wordMatches) {
        return option;
      }

      return null;
    };
    return newOptions?.filter(option => filterOptionsByWord(option));
  }, [newOptions, filterWord]);

  const handleClickOutside = useCallback(
    e => {
      if (selectRef.current.contains(e.target)) {
        // inside click
      } else {
        // outside click
        setIsOpen(false);
      }
    },
    [selectRef],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    setFilterWord('');
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const handleKeyPress = e => {
    if (e.key === 'Enter' && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      handleChange(filteredOptions[0]);
      setIsOpen(false);
    }
  };

  return (
    <StyledSelectWrapper
      isActive={isActive}
      onKeyPress={handleKeyPress}
      className={className}
      ref={selectRef}
      inputSize={size}
    >
      <StyledSelect
        readOnly
        type="text"
        placeholder={placeholder}
        size={size}
        error={error}
        dropdownDisabled={newOptions.length === 1 && selectedValue !== '' && hasOnlyUnitsWithSubgroup}
        value={selectedLabel || ''}
        disabled={disabled}
        onClick={openDropDownContent}
        data-cy={`toggle-${dataCy}`}
        inputDifferent={inputDifferent} // Prop para controlar estilos diferentes no input
        inputWidth={inputWidth} // Prop para controlar a largura do input
      />
      <Conditional
        when={!disabled && !isRequired && selectedValue !== '' && newOptions?.length > 0 && isClearInputActive}
      >
        <CleanInputIcon
          size={size}
          onClick={() => {
            handleChange({ value: '', label: '' });
            if (name === 'unitGroup') {
              cleanFields();
            }
            onInputClear();
          }}
        />
      </Conditional>
      <StyledDropdownContent isOpen={isOpen}>
        {isSearchDisabled !== true && (
          <StyledSearchBar inputDifferent={inputDifferent}>
            <Input
              type="text"
              autoComplete="off"
              name="search"
              size="extraSmall"
              placeholder="Pesquisar"
              icon={MagnifyingIcon}
              data-cy={`search-${dataCy}`}
              ref={selectRef}
              onChange={e => setFilterWord(e.target.value)}
            />
          </StyledSearchBar>
        )}
        <StyledDropdownContentWrapper>
          {filteredOptions.map(option => (
            <StyledDropdownItem
              key={option.value}
              data-cy={`${dataCy}-option`}
              onClick={() => {
                setIsOpen(false);
                handleChange(option);
                onSelect(option);
              }}
              inputDifferent={inputDifferent} // Prop para controlar estilos diferentes no input
            >
              <DropdownOption />
              <label title={option.label}>{option.label}</label>
            </StyledDropdownItem>
          ))}
          {children}
        </StyledDropdownContentWrapper>
      </StyledDropdownContent>
      <StyledError>
        <p>{error && errormessage}</p>
      </StyledError>
    </StyledSelectWrapper>
  );
});

Select.propTypes = {
  size: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  options: PropTypes.array,
  defaultValue: PropTypes.any,
  isClearInputActive: PropTypes.bool,
  inputDifferent: PropTypes.bool, // Nova propriedade para controlar estilos diferentes no input
  inputWidth: PropTypes.string, // Nova propriedade para controlar a largura do input
};

Select.defaultProps = {
  size: 'medium',
  isClearInputActive: true,
  options: [],
  defaultValue: '',
};

export { Select };
